import { Component, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ErrorBoundaryStore } from '../error/error-boundary.store';
import {
  clearChunkRecoveryMarkers,
  isChunkLoadError,
} from '../error/chunk-load-error';
import { recoverStaleShell } from '../error/shell-recovery';
import { environment } from '../../../environments/environment';

/**
 * Fallback UI when {@link GlobalErrorHandler} reports a non-recoverable error.
 *
 * CTA contract:
 * - **Try Again** — dismisses the boundary and remounts the app shell in place.
 *   Useful for transient render/logic faults. For stale-chunk failures this is
 *   the same as Reload (in-place remount cannot fetch a deleted hashed file).
 * - **Reload Page** — always hard-recovers: unregister SW, wipe Cache Storage,
 *   navigate with `ngsw-bypass`. A plain `location.reload()` would ask the same
 *   worker for the same missing chunk and loop on this screen.
 *
 * If hard recover cannot run (offline) or the fault returns after recover, this
 * same screen is the fallback — never a silent no-op.
 */
@Component({
  selector: 'app-error-fallback',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    <div class="app-min-viewport flex flex-col items-center justify-center bg-primary px-6 text-center">
      <div class="max-w-md rounded-2xl border border-border-overlay bg-surface p-10 shadow-xl">
        <h2 class="mb-4 text-2xl font-bold text-text-main">{{ 'common.errorBoundary.title' | translate }}</h2>
        <p class="mb-6 text-text-secondary">{{ 'common.errorBoundary.body' | translate }}</p>

        @if (!environment.production && errorMessage()) {
          <pre class="mb-6 max-h-40 overflow-auto rounded-lg bg-overlay-dark p-4 text-left text-xs text-error">{{ errorMessage() }}</pre>
        }

        @if (recoveryHint()) {
          <p class="mb-4 text-sm font-medium leading-5 text-text-secondary" role="status">
            {{ recoveryHint() | translate }}
          </p>
        }

        <div class="flex justify-center gap-4">
          <button
            type="button"
            (click)="tryAgain()"
            class="rounded-xl bg-accent px-6 py-3 font-semibold text-text-main transition-colors hover:bg-accent-hover"
          >
            {{ 'common.errorBoundary.tryAgain' | translate }}
          </button>
          <button
            type="button"
            (click)="reload()"
            class="rounded-xl border border-border-overlay px-6 py-3 font-semibold text-text-main transition-colors hover:bg-overlay-light"
          >
            {{ 'common.errorBoundary.reloadPage' | translate }}
          </button>
        </div>
      </div>
    </div>
  `,
})
export class ErrorFallbackComponent {
  store = inject(ErrorBoundaryStore);
  environment = environment;

  /** Shown when a CTA cannot leave this screen (e.g. offline hard recover). */
  readonly recoveryHint = signal<string | null>(null);

  errorMessage(): string {
    const err = this.store.error();
    return err instanceof Error ? err.message : String(err ?? '');
  }

  tryAgain(): void {
    this.recoveryHint.set(null);
    if (isChunkLoadError(this.errorMessage())) {
      void this.reload();
      return;
    }
    // Soft remount. If the same fault throws again, GlobalErrorHandler re-reports
    // and this screen comes back — that is the handled fallback.
    this.store.reset();
  }

  reload(): void {
    this.recoveryHint.set(null);
    if (!navigator.onLine) {
      // recoverStaleShell(force) refuses to drop the only cached copy offline, and
      // the boot-fallback UI is hidden while app-root is mounted — stay here.
      this.recoveryHint.set('common.errorBoundary.offlineHint');
      return;
    }
    clearChunkRecoveryMarkers();
    void recoverStaleShell(true);
  }
}
